import type { PdfProductId } from "@/constants/pdf-products";
import { passTypeForProductId } from "@/types/pass";

export const NICEPAY_GOODS_NAME_SUFFIX = "(정보처리기사 학습용)";
export const NICEPAY_GOODS_NAME_MAX_BYTES = 40;

const NICEPAY_PDF_GOODS_NAMES = {
  "26-3-compressed-pdf": `26-3 이론 PDF ${NICEPAY_GOODS_NAME_SUFFIX}`,
  "26-3-coding-pdf": `26-3 코딩 PDF ${NICEPAY_GOODS_NAME_SUFFIX}`,
  "26-1-compressed-pdf": `26-1·2 이론 PDF ${NICEPAY_GOODS_NAME_SUFFIX}`,
  "26-1-coding-pdf": `26-1·2 코딩 PDF ${NICEPAY_GOODS_NAME_SUFFIX}`,
  "25-3-super-compressed-pdf": `25-3 이론 초압축본 ${NICEPAY_GOODS_NAME_SUFFIX}`,
  "25-3-compressed-pdf": `25-3 이론 압축본 ${NICEPAY_GOODS_NAME_SUFFIX}`,
  "25-2-super-compressed-pdf": `25-2 이론 초압축본 ${NICEPAY_GOODS_NAME_SUFFIX}`,
} as const satisfies Record<PdfProductId, string>;

const NICEPAY_PASS_GOODS_NAMES = {
  starter: `Starter 이용권 ${NICEPAY_GOODS_NAME_SUFFIX}`,
  premium: `Premium 이용권 ${NICEPAY_GOODS_NAME_SUFFIX}`,
  complete: `Complete 이용권 ${NICEPAY_GOODS_NAME_SUFFIX}`,
  intelligence: `Intelligence ${NICEPAY_GOODS_NAME_SUFFIX}`,
} as const;

/**
 * Nicepay 문서의 40-byte 제한에 맞춰 제어된 상품명의 길이를 계산한다.
 * 현재 상품명은 ASCII와 EUC-KR 2-byte 문자(한글·가운데점)만 사용한다.
 */
export function getNicepayGoodsNameByteLength(value: string): number {
  let bytes = 0;
  for (const character of value) {
    bytes += character.codePointAt(0)! <= 0x7f ? 1 : 2;
  }
  return bytes;
}

function assertNicepayGoodsName(name: string): string {
  const byteLength = getNicepayGoodsNameByteLength(name);
  if (byteLength > NICEPAY_GOODS_NAME_MAX_BYTES) {
    throw new Error(
      `Nicepay goodsName이 ${NICEPAY_GOODS_NAME_MAX_BYTES}byte를 초과했습니다: ${byteLength}byte`
    );
  }
  return name;
}

/** productId에 대응하는 영수증용 Nicepay 상품명을 반환한다. */
export function getNicepayGoodsName(productId: string): string {
  const passType = passTypeForProductId(productId);
  if (passType) {
    return assertNicepayGoodsName(NICEPAY_PASS_GOODS_NAMES[passType]);
  }

  if (Object.prototype.hasOwnProperty.call(NICEPAY_PDF_GOODS_NAMES, productId)) {
    return assertNicepayGoodsName(
      NICEPAY_PDF_GOODS_NAMES[productId as PdfProductId]
    );
  }

  throw new Error(`Nicepay 상품명이 등록되지 않은 상품입니다: ${productId}`);
}
